import { useEffect, useMemo, useState } from "react";
import { Save, RotateCcw, Search, Globe } from "lucide-react";
import { toast } from "sonner";
import { useSiteSettings } from "../../context/SiteSettingsContext";
import ImagePicker from "./ImagePicker";

const FIELDS = [
  ["company_name", "Company Name"],
  ["tagline", "Tagline"],
  ["default_title", "Default Title"],
  ["title_template", "Title Template"],
  ["default_description", "Default Description", "textarea"],
  ["default_keywords", "Keywords"],
  ["site_url", "Canonical Site URL"],
  ["twitter_handle", "Twitter Handle"],
];

export default function SeoSettingsEditor() {
  const { settings, saveSettings } = useSiteSettings();
  const [draft, setDraft] = useState(settings?.seo);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(settings?.seo);
  }, [settings?.seo]);

  const dirty = useMemo(
    () => JSON.stringify(draft || {}) !== JSON.stringify(settings?.seo || {}),
    [draft, settings?.seo],
  );

  const setField = (key, value) => {
    setDraft((prev) => ({ ...(prev || {}), [key]: value }));
  };

  const reset = () => setDraft(settings?.seo);

  const save = async () => {
    setSaving(true);
    try {
      await saveSettings({ seo: draft });
      toast.success("SEO settings saved.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not save SEO settings.");
    } finally {
      setSaving(false);
    }
  };

  if (!draft) return null;

  return (
    <div className="space-y-6" data-testid="seo-settings-editor">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="eyebrow mb-2">SEO SETTINGS</div>
          <h2 className="font-display text-2xl md:text-3xl font-bold text-[var(--text-primary)]">Metadata and search visibility</h2>
          <p className="text-sm text-[var(--text-secondary)] mt-2 max-w-2xl">
            Manage default metadata, Open Graph cards, Twitter cards, canonical URLs and crawler directives.
          </p>
        </div>
        <div className="flex gap-3">
          <button type="button" className="btn-secondary" onClick={reset} data-testid="seo-settings-reset">
            <RotateCcw className="w-4 h-4" /> Reset
          </button>
          <button type="button" className="btn-primary disabled:opacity-50" disabled={!dirty || saving} onClick={save} data-testid="seo-settings-save">
            <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save SEO Settings"}
          </button>
        </div>
      </div>

      <div className="grid xl:grid-cols-[1fr_0.8fr] gap-6">
        <div className="bg-white rounded-3xl border border-[var(--border-light)] p-6">
          <div className="flex items-center gap-2 mb-5">
            <Search className="w-4 h-4 text-[var(--gold)]" />
            <div className="font-display text-xl font-bold text-[var(--text-primary)]">Search Metadata</div>
          </div>
          <div className="grid gap-5">
            {FIELDS.map(([key, label, type]) => (
              <div key={key}>
                <label className="label-x">{label}</label>
                {type === "textarea" ? (
                  <textarea className="input-x min-h-[120px]" value={draft?.[key] || ""} onChange={(e) => setField(key, e.target.value)} data-testid={`seo-${key}`} />
                ) : (
                  <input className="input-x" value={draft?.[key] || ""} onChange={(e) => setField(key, e.target.value)} data-testid={`seo-${key}`} />
                )}
              </div>
            ))}
            <div>
              <label className="label-x">Robots Directive</label>
              <select className="input-x" value={draft?.robots || "index,follow"} onChange={(e) => setField("robots", e.target.value)} data-testid="seo-robots">
                <option value="index,follow">index,follow</option>
                <option value="noindex,nofollow">noindex,nofollow</option>
              </select>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-3xl border border-[var(--border-light)] p-6 space-y-5">
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-[var(--gold)]" />
              <div className="font-display text-xl font-bold text-[var(--text-primary)]">Social Preview Image</div>
            </div>
            <ImagePicker value={draft?.og_image_url} onChange={(value) => setField("og_image_url", value)} aspect="1.91/1" />
            <p className="text-xs text-[var(--text-secondary)]">
              Recommended for Open Graph and Twitter previews. Use a wide image for the best result.
            </p>
          </div>

          <div className="bg-white rounded-3xl border border-[var(--border-light)] p-6">
            <div className="eyebrow mb-3">PREVIEW</div>
            <div className="rounded-3xl border border-[var(--border-light)] overflow-hidden">
              <div className="aspect-[1.91/1] bg-[var(--bg-secondary)]">
                {draft?.og_image_url ? (
                  <img src={draft.og_image_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${draft.og_image_url}` : draft.og_image_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[var(--text-muted)] text-sm">OG image preview</div>
                )}
              </div>
              <div className="p-5 bg-white">
                <div className="text-[var(--gold)] text-xs font-semibold mb-2">{draft?.site_url || settings?.seo?.site_url}</div>
                <div className="font-display text-xl font-bold text-[var(--text-primary)]">{draft?.company_name || settings?.seo?.company_name}</div>
                <p className="text-sm text-[var(--text-secondary)] mt-2">{draft?.tagline || settings?.seo?.tagline}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

